import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  popupNotificationsQueryKey,
  unreadNotificationsCountQueryKey,
} from "@/lib/constants";
import { guacHttpClient } from "@/lib/http";
import {
  HttpResponse,
  MarkNotificationsReadByCandidacyResponse,
  NotificationResponse,
} from "@/types";

// Opening a candidacy's sheet reads the mentions and excellent-candidate nudge
// for that candidacy. The server decides which rows qualify and returns them, so
// the caches can be patched instead of refetched.
export const useUpdateSetNotificationsReadByCandidacy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (candidacyId: string) => {
      const path = `notification-recipients/read-by-candidacy/${candidacyId}`;
      const res = await guacHttpClient.patch<
        MarkNotificationsReadByCandidacyResponse,
        HttpResponse<MarkNotificationsReadByCandidacyResponse>,
        undefined
      >(path, undefined);
      return res.data;
    },
    onSuccess: ({ notificationIds }) => {
      if (notificationIds.length === 0) return;

      const readIds = new Set(notificationIds);
      const now = new Date().toISOString();

      queryClient.setQueryData<NotificationResponse[]>(
        ["notifications"],
        (cachedData) =>
          cachedData?.map((notification) => {
            if (!readIds.has(notification.id)) return notification;
            return { ...notification, readAt: now, dismissedAt: now };
          }),
      );

      queryClient.setQueryData<NotificationResponse[]>(
        popupNotificationsQueryKey,
        (cachedData) =>
          cachedData?.filter((notification) => !readIds.has(notification.id)),
      );

      void queryClient.invalidateQueries({
        queryKey: unreadNotificationsCountQueryKey,
      });
    },
  });
};
